<template>
  <div id="kjk-registration">
    <!-- Main Header -->
    <b-navbar
      id="main-header"
      type="dark"
      variant="primary"
      toggleable="lg"
      sticky>
      <!-- Event Host -->
      <b-navbar-brand>
        <h3 v-if="event.id" class="m-0">{{event.org_name}}</h3>
        <div
          id="logo"
          v-else>
          <img src="./assets/img/KJKRegistrations_White_Small.png" />
          Registrations
        </div>
      </b-navbar-brand>
      <!-- / Event Host -->

      <b-navbar-toggle target="nav-collapse" />

      <b-collapse
        id="nav-collapse"
        is-nav>
        <!-- Main Navigation -->
        <b-navbar-nav
          id="main-navigation"
          v-if="event.id">
          <!-- All Events Link -->
          <b-nav-item
            to="/register/events"
            :class="{
              active: $route.path.includes('/events')
            }">
            <font-awesome-icon icon="calendar-alt"></font-awesome-icon>
            All Events
          </b-nav-item>
          <!-- / All Events Link -->

          <!-- Select Pricing Link -->
          <b-nav-item
            :to="`/register/event/${event.id}${event.hidden ? `/${event.hidden_code}` : ''}`"
            :class="{
              active: $route.path.includes('/event/')
            }">
            <font-awesome-icon icon="dollar-sign"></font-awesome-icon>
            Select Pricing for {{event.name}}
          </b-nav-item>
          <!-- / Select Pricing Link -->

          <!-- Cart -->
          <b-nav-item
            @click="checkCostScenario(); goToCart();"
            :class="{
              active: $route.path.includes('cart') || $route.path.includes('checkout')
            }">
            <font-awesome-icon icon="shopping-cart"></font-awesome-icon>
            Cart ({{(registrationSession.registrations || []).length}})
          </b-nav-item>
          <!-- / Cart -->
        </b-navbar-nav>
        <!-- / Main Navigation -->

        <!-- User Account Menu -->
        <b-navbar-nav
          v-if="loggedInUser"
          class="ml-auto">

          <b-nav-item
            v-if="!loggedInUser.id"
            @click="showSignIn"
            right>
            Sign In
          </b-nav-item>

          <b-nav-item
            v-if="!loggedInUser.id"
            @click="showSignUp"
            right>
            Sign Up
          </b-nav-item>

          <b-nav-item-dropdown
            v-if="loggedInUser.id"
            right>
            <template slot="button-content">
              {{loggedInUser.full_name}}
            </template>

            <b-dropdown-header>My Account</b-dropdown-header>
            <b-dropdown-divider />

            <!-- Events With Balance Due Link -->
            <b-dropdown-item
              to="/balance-due-registrations">
              Balance Due ({{balanceDueRegistrations.length}})
            </b-dropdown-item>
            <!-- / Events With Balance Due Link -->

            <!-- Upcoming Events Link -->
            <b-dropdown-item
              to="/upcoming-events">
              Upcoming Events ({{upcomingRegistrations.length}})
            </b-dropdown-item>
            <!-- / Upcoming Events Link -->

            <!-- Profile Link -->
            <b-dropdown-item
              @click="openProfile">
              Update Profile
            </b-dropdown-item>
            <!-- / Profile Link -->

            <!-- Change Password -->
            <b-dropdown-item
              @click="openChangePassword">
              Update Password
            </b-dropdown-item>
            <!-- / Change Password -->

            <!-- Sign Out Link -->
            <b-dropdown-item @click="signOut">
              Sign Out
            </b-dropdown-item>
            <!-- / Sign Out Link -->
          </b-nav-item-dropdown>
        </b-navbar-nav>
        <!-- / User Account Menu -->
      </b-collapse>
    </b-navbar>
    <!-- / Main Header -->

    <!-- Main Content -->
    <div id="main-content">
      <router-view
        class="page-content"
        :key="$route.path"
      />
    </div>
    <!-- / Main Content -->

    <!-- Footer -->
    <footer class="border-top border-muted">
      &copy; 2019 KJK Registrations
    </footer>
    <!-- / Footer -->

    <!-- Global Loading Indicator -->
    <GlobalLoader variant="light" />
    <!-- / Global Loading Indicator -->

    <!-- Confirm Dialog -->
    <ConfirmDialog />
    <!-- / Confirm Dialog -->

    <!-- Warning Dialog -->
    <WarningDialog />
    <!-- / Warning Dialog -->

    <!-- Sign In Dialog -->
    <SignIn />
    <!-- / Sign In Dialog -->

    <!-- Update Profile -->
    <b-modal
      id="profile-modal"
      ref="profileModal"
      size="sm"
      title="Update Profile"
      @shown="focusInput('profileFirstName')"
      @hidden="profile = {}"
      v-model="profileModalOpen"
      hide-footer>
      <b-form
        id="profile-form"
        name="profileForm"
        @submit.prevent="submitProfile">
        <!-- First Name -->
        <div class="row">
          <div class="col-lg">
            <label for="profile-first-name">
              First Name
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              id="profile-first-name"
              ref="profileFirstName"
              name="profileFirstName"
              type="text"
              placeholder="Enter your first name"
              :disabled="!!profileLoadingMsg"
              v-model="profile.first_name">
            </b-form-input>
          </div>
        </div>
        <!-- / First Name -->

        <!-- Last Name -->
        <div class="row mt-3">
          <div class="col-lg">
            <label for="profile-last-name">
              Last Name
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              id="profile-last-name"
              ref="profileLastName"
              name="profileLastName"
              type="text"
              placeholder="Enter your last name"
              :disabled="!!profileLoadingMsg"
              v-model="profile.last_name">
            </b-form-input>
          </div>
        </div>
        <!-- / Last Name -->

        <!-- Email -->
        <div class="row mt-3">
          <div class="col-lg">
            <div class="d-flex justify-content-between align-items-center">
              <label for="profile-email">
                Email
                <small class="text-danger ml-1">required</small>
              </label>
              <div
                v-b-tooltip
                title="Updating your email address will also change how you sign in.">
                <font-awesome-icon
                  size="sm"
                  icon="info-circle">
                </font-awesome-icon>
              </div>
            </div>
            <b-form-input
              id="profile-email"
              ref="profileEmail"
              name="profileEmail"
              type="email"
              placeholder="Enter your email address"
              v-model="profile.email"
              :disabled="!!profileLoadingMsg">
            </b-form-input>
          </div>
        </div>
        <!-- / Email -->

        <!-- Form Buttons -->
        <div class="row justify-content-end mt-4">
          <div class="col-lg-auto pr-lg-0">
            <b-button
              class="w-100 w-lg-auto"
              type="submit"
              variant="success"
              :disabled="!!profileLoadingMsg">
              Submit
            </b-button>
          </div>

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              class="w-100 w-lg-auto"
              type="button"
              variant="danger"
              @click="profileModalOpen = false;"
              :disabled="!!profileLoadingMsg">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="profile-loading"
        variant="light"
        :msg="profileLoadingMsg">
      </Loader>
      <!-- / Loading Indicator -->
    </b-modal>
    <!-- Update Profile -->

    <!-- Change Password -->
    <b-modal
      id="change-password-modal"
      ref="changePasswordModal"
      title="Update Password"
      @shown="focusInput('changePasswordCurrent')"
      @hidden="changePassword = {}; showPasswords = false;"
      v-model="changePasswordModalOpen"
      hide-footer>
      <b-form
        id="change-password-form"
        name="changePasswordForm"
        @submit.prevent="submitChangePassword">

        <!-- Current Password -->
        <div class="row">
          <div class="col-lg-6">
            <label for="change-password-current">
              Current Password
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              v-if="!showPasswords"
              id="change-password-current"
              ref="changePasswordCurrent"
              name="changePasswordCurrent"
              type="password"
              placeholder="Enter your current password"
              v-model="changePassword.password"
              :disabled="!!changePasswordLoadingMsg">
            </b-form-input>
            <b-form-input
              v-if="showPasswords"
              id="change-password-current"
              ref="changePasswordCurrent"
              name="changePasswordCurrent"
              type="text"
              placeholder="Enter your current password"
              v-model="changePassword.password"
              :disabled="!!changePasswordLoadingMsg">
            </b-form-input>
          </div>
        </div>
        <!-- / Current Password -->

        <!-- New / Confirm Password -->
        <div class="row mt-3">
          <!-- New Password -->
          <div class="col-lg">
            <label for="change-password-new">
              New Password
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              v-if="!showPasswords"
              id="change-password-new"
              ref="changePasswordNew"
              name="changePasswordNew"
              type="password"
              placeholder="Enter your new password"
              v-model="changePassword.new_password"
              :disabled="!!changePasswordLoadingMsg">
            </b-form-input>
            <b-form-input
              v-if="showPasswords"
              id="change-password-new"
              ref="changePasswordNew"
              name="changePasswordNew"
              type="text"
              placeholder="Enter your new password"
              v-model="changePassword.new_password"
              :disabled="!!changePasswordLoadingMsg">
            </b-form-input>
          </div>
          <!-- / New Password -->

          <!-- Password Confirm -->
          <div class="col-lg mt-3 mt-lg-0">
            <label for="change-password-confirm">
              Confirm Password
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              v-if="!showPasswords"
              id="change-password-confirm"
              ref="profilePasswordConfirm"
              name="profilePasswordConfirm"
              type="password"
              placeholder="Confirm your new password"
              v-model="changePassword.new_password_2"
              :disabled="!!changePasswordLoadingMsg">
            </b-form-input>
            <b-form-input
              v-if="showPasswords"
              id="change-password-confirm"
              ref="profilePasswordConfirm"
              name="profilePasswordConfirm"
              type="text"
              placeholder="Confirm your new password"
              v-model="changePassword.new_password_2"
              :disabled="!!changePasswordLoadingMsg">
            </b-form-input>
          </div>
          <!-- / Password Confirm -->
        </div>
        <!-- / New / Confirm Password -->

        <!-- Form Buttons -->
        <div class="row align-items-center justify-content-end mt-4">
          <div class="col-lg-auto mr-lg-auto">
            <b-form-checkbox
              id="change-password-show"
              name="changePasswordShow"
              ref="changePasswordShow"
              v-model="showPasswords">
              Show Passwords
            </b-form-checkbox>
          </div>

          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <b-button
              class="w-100 w-lg-auto"
              type="submit"
              variant="success"
              :disabled="!!changePasswordLoadingMsg">
              Submit
            </b-button>
          </div>

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              class="w-100 w-lg-auto"
              type="button"
              variant="danger"
              @click="changePasswordModalOpen = false;"
              :disabled="!!changePasswordLoadingMsg">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="change-password-loading"
        variant="light"
        :msg="changePasswordLoadingMsg">
      </Loader>
      <!-- / Loading Indicator -->
    </b-modal>
    <!-- Change Password -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from './services/data';
import Session from './services/user-session';
import AppMixin from './services/mixin.app';
import LocalStorageMixin from './services/mixin.local-storage';

export default {
  name: 'App',
  mixins: [
    AppMixin,
    LocalStorageMixin,
  ],
  data() {
    return {
      // account
      profileModalOpen: false,
      profileLoadingMsg: '',
      profile: {},

      // change password
      changePasswordModalOpen: false,
      showPasswords: false,
      changePasswordLoadingMsg: '',
      changePassword: {},
    };
  },
  watch: {
    // eslint-disable-next-line
    $route: function (newRoute) {
      // if no logged in user and using non-auth route
      if (this.isUnauthorizedOnRoute(newRoute)) {
        this.goToEvent();
      }
    },
  },
  methods: {
    // account
    openProfile() {
      this.profileModalOpen = true;
      this.profile = _.pick(this.loggedInUser, [
        'first_name',
        'last_name',
        'email',
      ]);
    },
    submitProfile() {
      this.profileLoadingMsg = 'Updating profile...';
      return DataSvc.updateSelf(this.profile)
        .then((user) => {
          this.profileModalOpen = false;
          _.extend(this.loggedInUser, user);
          this.localStorageSet('user', this.loggedInUser);
        })
        .finally(() => {
          this.profileLoadingMsg = '';
        });
    },
    signOut() {
      const token = this.loggedInUser.auth_token;

      // clear memory
      this.clearLocalSession();

      // go to event
      this.goToEvent();

      // kill token
      return DataSvc.signOut(token).then(Session.end);
    },

    // change password
    openChangePassword() {
      this.changePasswordModalOpen = true;
    },
    submitChangePassword() {
      this.changePasswordLoadingMsg = 'Updating password...';
      return DataSvc.updateSelf(this.changePassword)
        .then(() => {
          this.changePasswordModalOpen = false;
        })
        .finally(() => {
          this.changePasswordLoadingMsg = '';
        });
    },
  },
  created() {
    // handle API errors
    DataSvc.configureErrorHandling((response) => {
      // form bulleted list of errors
      let errList = _.map(
        response.data.errors,
        e => `&bull; ${e.message}`,
      ).join('<br>');

      // if unauthorized response on any non-sign in request
      if (response.status === 401
          && !response.config.url.includes('users/auth_token')) {
        errList = '&bull; Your session has expired. Please sign in again.';
        this.$router.replace({ name: 'signin' });
        this.loggedInUser = {};
        Session.end();
      }

      if (response.status === 404) {
        this.$router.replace({ name: 'not-found' });
      } else {
        this.showWarning(errList, 'The following errors occured');
      }
    });

    // get event
    let { eventID, hiddenCode } = this.$route.params;
    let eventPromise = Promise.resolve();
    const sessionPromise = Session.resume()
      .then((data) => {
        [
          this.loggedInUser,
          this.registrationSession,
          this.closedRegistrations,
        ] = data;
      }, this.clearLocalSession);

    if (!eventID) {
      eventID = this.localStorageGet('eventID');
    }

    if (eventID) {
      eventID = parseInt(eventID);
      const hiddenEvent = _.find(this.hiddenEvents, { id: eventID });

      this.showGlobalLoader('Loading data...');

      if (hiddenEvent) {
        hiddenCode = hiddenEvent.hidden_code;
      }

      eventPromise = DataSvc.getEvent(eventID, hiddenCode)
        .then((event) => {
          let eventsPromise;

          if (event.id === eventID) {
            this.event = event;
            this.localStorageSet('eventID', event.id);
            this.organizationID = event.org_id;
            eventsPromise = DataSvc.getEvents(event.org_id);

            if (event.hidden && !_.find(this.hiddenEvents, { id: event.id })) {
              this.hiddenEvents = this.hiddenEvents.concat([event]);
            }
          } else {
            eventsPromise = Promise.reject(new Error('Invalid URL: Your hidden code does not match the event.'));
          }

          return eventsPromise;
        })
        .then((events) => {
          this.events = events;
        }, () => {
          this.organizationID = null;
        });
    } else {
      eventPromise = Promise.reject(new Error('No event detected.'));
    }

    eventPromise
      .catch((err) => {
        this.$router.replace({ name: 'not-found' });
        return Promise.reject(err);
      });

    this.initPromise = Promise.all([
      sessionPromise,
      eventPromise,
    ]).then(() => {
      // event found but no user on unauthorized page
      if (this.isUnauthorizedOnRoute(this.$route)) {
        this.goToEvent();
      }
    }).finally(this.hideGlobalLoader);
  },
};
</script>

<style lang="scss">
#kjk-registration {
  height: 100%;

  #logo {
    margin-top: 1px;
    font-size: 28px;
    line-height: 28px;

    img {
      height: 28px;
    }
  }

  footer {
    position: fixed;
    z-index: 999;
    width: 100%;
    bottom: 0;
    text-align: center;
    background: white;
    padding: 5px 0;
    height: 35px;
  }

  #main-content {
    padding-bottom: 35px;
  }

  #main-header {
    .nav-item {
      .nav-link {
        color: rgba(255,255,255,1);

        &:hover {
          color: rgba(255,255,255,.75);
        }
      }

      &.active {
        .nav-link {
          background: rgba(255,255,255,.15);
        }
      }
    }
  }
}
</style>
